import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'app/providers/AuthProvider';
import { useCurrentUserAccess } from 'shared/session/useCurrentUserRole';
import { forAttentionCue, retryWhileInUse } from 'shared/query/attention';
import {
  answerAsk,
  closeAgentMessage,
  createAgent,
  forwardAsk,
  deleteAgentChat,
  getAgent,
  getAgentAccessOptions,
  getExecutionCatalog,
  getAgentFeed,
  getAgentMemory,
  getInbox,
  getPeopleToAsk,
  getUpdates,
  listAgentChats,
  listAgents,
  markUpdatesSeen,
  saveAgentMemory,
  saveAgentNotes,
  updateAgent,
} from './api';

export const agentKeys = {
  all: ['agents'],
  team: () => ['agents', 'team'],
  detail: (agentId) => ['agents', 'detail', agentId],
  memory: (agentId) => ['agents', 'detail', agentId, 'memory'],
  feed: () => ['agents', 'feed'],
  inbox: () => ['agents', 'inbox'],
  updates: () => ['agents', 'updates'],
  chats: (agentId) => ['agents', 'chats', agentId],
};

export const FEED_INTERVAL_MS = 30_000;

const scopedRetry = (count, error) => ![401, 403, 404].includes(error?.status) && count < 2;

/** Personal responses must never survive into a different signed-in principal. */
function useScopedAgentQuery(options) {
  const { user } = useAuth();
  const access = useCurrentUserAccess();
  const authUserId = user?.id || user?.uid;
  const organizationId = access.data?.organizationId;
  const profileId = access.data?.profileId;
  const ready = Boolean(authUserId && organizationId && profileId && !access.isError);
  const scope = ready ? { authUserId, organizationId, profileId } : null;
  const query = useQuery({
    gcTime: 0,
    retry: scopedRetry,
    ...options,
    queryKey: [...options.queryKey, scope],
    enabled: ready && options.enabled !== false,
    queryFn: (context) => {
      // Consume the signal so React Query cancels and discards an abandoned
      // principal's request even when the transport cannot abort its fetch.
      context.signal.throwIfAborted();
      return options.queryFn(context);
    },
  });
  return !ready || [401, 403, 404].includes(query.error?.status)
    ? { ...query, data: undefined }
    : query;
}

/** The AI org chart the caller can see, with each agent's live status. */
export const useTeam = ({ enabled = true, directory = false } = {}) =>
  useScopedAgentQuery({
    queryKey: [...agentKeys.team(), { directory }],
    queryFn: () => listAgents({ directory }),
    enabled,
    staleTime: 10_000,
    // Status rings come from the feed; the roster itself rarely changes.
    refetchInterval: 120_000,
  });

export const useTeamAgents = (options) => {
  const query = useTeam(options);
  const agents = query.data?.agents || [];
  return {
    ...query,
    agents,
    lead: agents.find((agent) => agent.is_lead) || agents.find((agent) => agent.runtime_support?.chat !== false && agent.runtime_kind !== 'external') || null,
    canManage: Boolean(query.data?.can_manage),
  };
};

export const useAgentProfile = (agentId) =>
  useScopedAgentQuery({
    queryKey: agentKeys.detail(agentId),
    queryFn: () => getAgent(agentId),
    enabled: Boolean(agentId),
    staleTime: 5_000,
    refetchInterval: 20_000,
  });

export const useAgentAccessOptions = (agentId) => useScopedAgentQuery({
  queryKey: [...agentKeys.detail(agentId), 'access-options'],
  queryFn: () => getAgentAccessOptions(agentId),
  enabled: Boolean(agentId),
});

export const useAgentMemory = (agentId) =>
  useScopedAgentQuery({
    queryKey: agentKeys.memory(agentId),
    queryFn: () => getAgentMemory(agentId),
    enabled: Boolean(agentId),
  });

/** Polled by the shell for status rings, the needs-you badge and unseen updates. */
export const useAgentFeed = ({ enabled = true } = {}) =>
  useScopedAgentQuery({
    queryKey: agentKeys.feed(),
    queryFn: getAgentFeed,
    enabled,
    staleTime: 5_000,
    refetchInterval: () => forAttentionCue(FEED_INTERVAL_MS),
    refetchIntervalInBackground: true,
    retry: retryWhileInUse(scopedRetry),
  });

export const useInbox = ({ enabled = true } = {}) =>
  useScopedAgentQuery({
    queryKey: agentKeys.inbox(),
    queryFn: getInbox,
    enabled,
    staleTime: 5_000,
    // The feed polls more often and refreshes this list when its Needs you count changes.
    refetchInterval: () => forAttentionCue(60_000),
    refetchIntervalInBackground: true,
    retry: retryWhileInUse(scopedRetry),
  });

/** What agents told the person outside a chat: progress, run reports, greetings. */
export const useUpdates = ({ enabled = true, limit = 20 } = {}) =>
  useScopedAgentQuery({
    queryKey: [...agentKeys.updates(), limit],
    queryFn: () => getUpdates({ limit }),
    enabled,
    staleTime: 5_000,
    refetchInterval: 60_000,
  });

export const useMarkUpdatesSeen = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markUpdatesSeen,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentKeys.feed() });
      queryClient.invalidateQueries({ queryKey: agentKeys.updates() });
    },
  });
};

/**
 * The person's chats with one agent, newest first (like ChatGPT's history).
 * `poll`: the list is on screen; otherwise it is read on demand and after each reply.
 */
export const useAgentChats = (agentId, { enabled = true, scopeKey = null, poll = true } = {}) =>
  useScopedAgentQuery({
    queryKey: [...agentKeys.chats(agentId), scopeKey],
    queryFn: () => listAgentChats(agentId),
    enabled: enabled && Boolean(agentId),
    staleTime: 10_000,
    select: (data) => data?.sessions || [],
    refetchInterval: poll ? 12_000 : false,
    retry: (count, error) => ![401, 403, 404].includes(error?.status) && count < 2,
  });

export const useDeleteAgentChat = (agentId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId) => deleteAgentChat(sessionId, agentId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: agentKeys.chats(agentId) }),
  });
};

const useInvalidateAgents = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: agentKeys.all });
};

export const useCreateAgent = () => {
  const invalidate = useInvalidateAgents();
  return useMutation({ mutationFn: createAgent, onSuccess: invalidate });
};

export const useUpdateAgent = (agentId) => {
  const invalidate = useInvalidateAgents();
  return useMutation({ mutationFn: (payload) => updateAgent(agentId, payload), onSuccess: invalidate });
};

export const useSaveAgentMemory = (agentId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ scope, content, expectedRevision }) =>
      scope === 'private'
        ? saveAgentNotes(agentId, content, expectedRevision)
        : saveAgentMemory(agentId, content, expectedRevision),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: agentKeys.memory(agentId) }),
  });
};

export const useAnswerAsk = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ askId, message, choice, files }) => answerAsk(askId, { message, choice, files }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentKeys.inbox() });
      queryClient.invalidateQueries({ queryKey: agentKeys.feed() });
      queryClient.invalidateQueries({ queryKey: ['initiatives'] });
    },
  });
};

export const useCloseAgentMessage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (askId) => closeAgentMessage(askId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentKeys.inbox() });
      queryClient.invalidateQueries({ queryKey: agentKeys.feed() });
    },
  });
};

/** Who a question can be passed to, and whether this person can invite someone new. */
export const usePeopleToAsk = ({ enabled = true } = {}) =>
  useScopedAgentQuery({ queryKey: ['agents', 'people-to-ask'], queryFn: getPeopleToAsk, enabled, staleTime: 60_000 });

export const useForwardAsk = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ askId, ...person }) => forwardAsk(askId, person),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentKeys.inbox() });
      queryClient.invalidateQueries({ queryKey: agentKeys.feed() });
    },
  });
};

export const useExecutionCatalog = ({ enabled = true } = {}) => useScopedAgentQuery({
  queryKey: ['agents', 'execution-catalog'], queryFn: getExecutionCatalog, enabled,
});
